/*
 * QREAR Audio Recorder — browser side of the storefront audio capture surface.
 *
 * Records with MediaRecorder, transcodes the take to MP3 (mono, 128 kbps) with
 * the vendored `lamejs` bundle, and injects the result into the add-to-cart form
 * as the line-item property `properties[audio_url]` (via DataTransfer), together
 * with `properties[_memora_type]=audio` and `properties[title]`. The extractor
 * backend reads exactly those three property keys.
 *
 * Requires: theme/assets/lamejs.min.js (global `lamejs`, LGPL-3.0). See
 *           theme/assets/lamejs.LICENSE.txt for the third-party notice.
 *
 * Public API — exactly one global is exposed:
 *   window.QrearAudioRecorder.init(config)
 *   config: {
 *     form?:        HTMLFormElement   // defaults to the cart add form
 *     titleInputId?: string           // default 'audio-title-input'
 *     fileInputId?:  string           // default 'audio-file-input'
 *     micButtonId?:  string           // default 'audio-mic-button'
 *     statusId?:     string           // default 'audio-status'
 *     errorId?:      string           // default 'audio-error'
 *     fileInfoId?:   string           // default 'audio-file-info'
 *     spectrumId?:   string           // default 'audio-spectrum'
 *     onStateChange?: (state) => void // optional, for the PR3 preview
 *     onFileChange?:  (file) => void  // optional, file|null, for the PR3 preview
 *   }
 *
 * While recording, a live real-time spectrum of the speaker's voice is rendered
 * as vertical bars inside the element referenced by `spectrumId` (the mic
 * button), fed by an AnalyserNode connected to the microphone stream.
 *
 *   init() is idempotent per form (guarded) and returns undefined.
 *
 * State machine: idle -> recording -> encoding -> done | error
 *
 * Open Question #3 decision (openspec/changes/product-audio/design.md):
 *   Some ogg/wav files report `Infinity`/`NaN` from `<audio>.duration`, so the
 *   3-minute rule cannot be enforced client-side for those uploads. Deterministic
 *   behavior implemented here: format and size are ALWAYS enforced; when the
 *   duration is unreadable the upload is ACCEPTED under a size-only duration
 *   guard and the limitation is surfaced in the status text. Recorded takes are
 *   always <= 180 s because recording hard-stops at three minutes.
 */
(function () {
  'use strict';

  var MAX_BYTES = 20 * 1024 * 1024; // 20 MB
  var MAX_SECONDS = 180; // 3 minutes
  var MP3_BITRATE = 128; // kbps, mono
  var MP3_CHUNK = 1152; // samples per lamejs frame
  var DURATION_PROBE_TIMEOUT = 5000; // ms

  var SPECTRUM_MIN_HEIGHT = 3;
  var SPECTRUM_MAX_HEIGHT = 22;
  var SPECTRUM_BAR_WIDTH = 3;
  var SPECTRUM_BAR_GAP = 3;

  var ALLOWED_EXTS = ['mp3', 'wav', 'ogg'];
  var ALLOWED_MIMES = [
    'audio/mpeg',
    'audio/mp3',
    'audio/wav',
    'audio/x-wav',
    'audio/wave',
    'audio/ogg',
    'application/ogg',
  ];
  var RECORDER_MIMES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

  var STATE = { IDLE: 'idle', RECORDING: 'recording', ENCODING: 'encoding', DONE: 'done', ERROR: 'error' };

  var MSG = {
    format: 'Formato no permitido. Subí un archivo MP3, WAV u OGG.',
    size: 'El archivo supera los 20 MB. Elegí uno más liviano.',
    duration: 'El audio supera los 3 minutos. Elegí uno más corto.',
    title: 'Escribí un título para tu audio.',
    missing: 'Agregá un audio: subí un archivo o grabalo con el micrófono.',
    encoding: 'Esperá un momento, estamos procesando el audio.',
    attach: 'Tu navegador no permite adjuntar el audio. Probá con otro navegador.',
    record: 'No pudimos usar el micrófono. Revisá los permisos o subí un archivo.',
    encode: 'No pudimos procesar la grabación. Probá de nuevo o subí un archivo.',
    micOff: 'La grabación con micrófono no está disponible en este navegador. Podés subir un archivo.',
    probe: 'Verificando la duración…',
    doneUpload: 'Audio listo.',
    doneUnknown: 'Audio listo (no pudimos verificar la duración; se validan formato y tamaño).',
    doneRecording: 'Grabación lista.',
    recording: 'Grabando… tocá el botón para detener.',
  };

  function init(config) {
    config = config || {};

    var form = config.form || findCartForm();
    if (!form) return;
    if (form.dataset && form.dataset.qrearAudio === '1') return;
    if (form.dataset) form.dataset.qrearAudio = '1';

    var ids = {
      title: config.titleInputId || 'audio-title-input',
      file: config.fileInputId || 'audio-file-input',
      mic: config.micButtonId || 'audio-mic-button',
      status: config.statusId || 'audio-status',
      error: config.errorId || 'audio-error',
      fileInfo: config.fileInfoId || 'audio-file-info',
      spectrum: config.spectrumId || 'audio-spectrum',
    };

    var titleInput = document.getElementById(ids.title);
    var fileInput = document.getElementById(ids.file);
    var micButton = document.getElementById(ids.mic);
    var statusEl = document.getElementById(ids.status);
    var errorEl = document.getElementById(ids.error);
    var fileInfoEl = document.getElementById(ids.fileInfo);
    var spectrumEl = document.getElementById(ids.spectrum);

    // Hidden property mirrors live INSIDE the add-to-cart form (pet pattern).
    var mirrors = {
      type: ensureMirror(form, '_memora_type'),
      title: ensureMirror(form, 'title'),
      audio: ensureAudioMirror(form),
    };
    if (titleInput) titleInput.removeAttribute('name');
    if (fileInput) fileInput.removeAttribute('name');
    if (mirrors.title && titleInput) mirrors.title.value = titleInput.value || '';

    var state = STATE.IDLE;
    var attached = { file: null, duration: null };
    var lastError = '';
    var defaultStatus = (statusEl && statusEl.textContent ? statusEl.textContent : '').trim();
    var recorder = null;
    var stream = null;
    var spectrumCtx = null;
    var spectrumSource = null;
    var spectrumAnalyser = null;
    var spectrumData = null;
    var spectrumBars = null;
    var spectrumRaf = null;
    var chunks = [];
    var cutoffTimer = null;
    var takeCancelled = false;
    var pickToken = 0;
    var micSupported = isRecordingSupported();

    function notifyState() {
      if (typeof config.onStateChange === 'function') {
        try {
          config.onStateChange(state);
        } catch (e) {}
      }
    }

    function setStatus(text) {
      if (statusEl) statusEl.textContent = text || '';
    }

    function showError(text) {
      lastError = text || '';
      if (errorEl) {
        errorEl.textContent = lastError;
        errorEl.hidden = !lastError;
      }
    }

    function clearError() {
      showError('');
    }

    function setState(next, message) {
      state = next;
      if (micButton) {
        micButton.setAttribute('data-qrear-mic-state', next);
        micButton.setAttribute('aria-label', next === STATE.RECORDING ? 'Detener grabación' : 'Grabar con micrófono');
      }
      if (message != null) setStatus(message);
      notifyState();
    }

    function setError(message) {
      showError(message);
      state = STATE.ERROR;
      if (micButton) micButton.setAttribute('data-qrear-mic-state', state);
      notifyState();
    }

    function updateFileInfo(file) {
      if (!fileInfoEl) return;
      if (!file) {
        fileInfoEl.textContent = fileInfoEl.getAttribute('data-default') || 'MP3, WAV u OGG. Máx 3 minutos y 20 MB.';
        return;
      }
      fileInfoEl.textContent = file.name + ' (' + Math.max(1, Math.round(file.size / 1024)) + ' KB)';
    }

    function attach(file, duration) {
      try {
        var dt = new DataTransfer();
        dt.items.add(file);
        mirrors.audio.files = dt.files;
      } catch (e) {
        attached = { file: null, duration: null };
        setError(MSG.attach);
        return false;
      }
      attached = { file: file, duration: duration == null ? null : duration };
      if (typeof config.onFileChange === 'function') {
        try {
          config.onFileChange(file);
        } catch (e) {}
      }
      return true;
    }

    function detach() {
      attached = { file: null, duration: null };
      try {
        var dt = new DataTransfer();
        mirrors.audio.files = dt.files;
      } catch (e) {
        try {
          mirrors.audio.value = '';
        } catch (e2) {}
      }
      if (typeof config.onFileChange === 'function') {
        try {
          config.onFileChange(null);
        } catch (e) {}
      }
    }

    function hasAudio() {
      return !!(attached.file || (mirrors.audio.files && mirrors.audio.files.length));
    }

    function spectrumBarCount() {
      var width = spectrumEl ? spectrumEl.clientWidth : 0;
      if (!width) return 24;
      var count = Math.round(width / (SPECTRUM_BAR_WIDTH + SPECTRUM_BAR_GAP));
      if (count < 12) return 12;
      if (count > 32) return 32;
      return count;
    }

    function resetSpectrumBars() {
      if (!spectrumBars) return;
      for (var i = 0; i < spectrumBars.length; i++) {
        spectrumBars[i].style.height = SPECTRUM_MIN_HEIGHT + 'px';
      }
    }

    function renderSpectrum() {
      if (!spectrumAnalyser || !spectrumData || !spectrumBars) {
        spectrumRaf = null;
        return;
      }
      spectrumRaf = window.requestAnimationFrame(renderSpectrum);
      spectrumAnalyser.getByteFrequencyData(spectrumData);
      var bars = spectrumBars.length;
      var bins = spectrumData.length;
      var minBin = 2;
      var maxBin = Math.max(minBin + 1, Math.floor(bins * 0.55));
      for (var i = 0; i < bars; i++) {
        // Logarithmic-ish mapping: voice energy sits in the low bins.
        var ratio = bars > 1 ? i / (bars - 1) : 0;
        var bin = Math.min(maxBin, Math.round(minBin + Math.pow(ratio, 1.8) * (maxBin - minBin)));
        var value = spectrumData[bin] / 255;
        value = value <= 0.05 ? 0 : (value - 0.05) / 0.95;
        spectrumBars[i].style.height =
          (SPECTRUM_MIN_HEIGHT + Math.pow(value, 0.75) * (SPECTRUM_MAX_HEIGHT - SPECTRUM_MIN_HEIGHT)).toFixed(1) + 'px';
      }
    }

    function startSpectrum(sourceStream) {
      stopSpectrum();
      if (!spectrumEl || !sourceStream) return;
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      try {
        spectrumCtx = new Ctx();
        spectrumSource = spectrumCtx.createMediaStreamSource(sourceStream);
        spectrumAnalyser = spectrumCtx.createAnalyser();
        spectrumAnalyser.fftSize = 512;
        spectrumAnalyser.smoothingTimeConstant = 0.75;
        spectrumSource.connect(spectrumAnalyser);
        spectrumData = new Uint8Array(spectrumAnalyser.frequencyBinCount);
      } catch (e) {
        stopSpectrum();
        return;
      }
      // Never connect the analyser to `destination`: the mic would feed back.
      if (spectrumCtx.state === 'suspended' && typeof spectrumCtx.resume === 'function') {
        try {
          spectrumCtx.resume();
        } catch (e) {}
      }
      var count = spectrumBarCount();
      if (!spectrumBars || spectrumBars.length !== count) {
        spectrumEl.innerHTML = '';
        spectrumBars = [];
        for (var i = 0; i < count; i++) {
          var bar = document.createElement('span');
          bar.className = 'qrear-audio-capture__spectrum-bar';
          bar.style.height = SPECTRUM_MIN_HEIGHT + 'px';
          spectrumEl.appendChild(bar);
          spectrumBars.push(bar);
        }
      }
      spectrumRaf = window.requestAnimationFrame(renderSpectrum);
    }

    function stopSpectrum() {
      if (spectrumRaf != null) {
        try {
          window.cancelAnimationFrame(spectrumRaf);
        } catch (e) {}
        spectrumRaf = null;
      }
      if (spectrumSource) {
        try {
          spectrumSource.disconnect();
        } catch (e) {}
        spectrumSource = null;
      }
      spectrumAnalyser = null;
      spectrumData = null;
      if (spectrumCtx) {
        try {
          spectrumCtx.close();
        } catch (e) {}
        spectrumCtx = null;
      }
      resetSpectrumBars();
    }

    function stopStream() {
      stopSpectrum();
      if (stream) {
        try {
          stream.getTracks().forEach(function (track) {
            track.stop();
          });
        } catch (e) {}
        stream = null;
      }
    }

    function discardRecording() {
      takeCancelled = true;
      if (cutoffTimer) {
        clearTimeout(cutoffTimer);
        cutoffTimer = null;
      }
      var current = recorder;
      recorder = null;
      if (current && current.state !== 'inactive') {
        try {
          current.stop();
        } catch (e) {}
      }
      stopStream();
      chunks = [];
    }

    function stopRecording() {
      if (cutoffTimer) {
        clearTimeout(cutoffTimer);
        cutoffTimer = null;
      }
      if (recorder && recorder.state !== 'inactive') {
        try {
          recorder.stop();
        } catch (e) {}
      }
      stopStream();
    }

    function onRecorderStop() {
      stopStream();
      var cancelled = takeCancelled;
      takeCancelled = false;
      var localChunks = chunks;
      chunks = [];
      recorder = null;
      if (cancelled) return;
      if (!localChunks.length) {
        setError(MSG.record);
        return;
      }
      var blob = new Blob(localChunks, { type: (localChunks[0] && localChunks[0].type) || 'audio/webm' });
      setState(STATE.ENCODING, 'Procesando el audio…');
      encodeRecording(blob)
        .then(function (mp3Blob) {
          var file = new File([mp3Blob], 'recording.mp3', { type: 'audio/mpeg' });
          if (!attach(file, null)) return;
          updateFileInfo(file);
          setState(STATE.DONE, MSG.doneRecording + ' (' + Math.max(1, Math.round(file.size / 1024)) + ' KB).');
        })
        .catch(function () {
          detach();
          updateFileInfo(null);
          setError(MSG.encode);
        });
    }

    function beginRecording(s) {
      stream = s;
      takeCancelled = false;
      var mime = pickRecorderMime();
      try {
        recorder = mime ? new window.MediaRecorder(s, { mimeType: mime }) : new window.MediaRecorder(s);
      } catch (e) {
        stopStream();
        setError(MSG.record);
        return;
      }
      chunks = [];
      recorder.addEventListener('dataavailable', function (e) {
        if (e.data && e.data.size) chunks.push(e.data);
      });
      recorder.addEventListener('stop', onRecorderStop);
      recorder.addEventListener('error', function () {
        takeCancelled = true;
      });
      try {
        recorder.start();
      } catch (e) {
        stopStream();
        setError(MSG.record);
        return;
      }
      setState(STATE.RECORDING, MSG.recording);
      startSpectrum(s);
      cutoffTimer = setTimeout(stopRecording, MAX_SECONDS * 1000);
    }

    function startRecording() {
      clearError();
      if (fileInput && fileInput.files && fileInput.files.length) {
        try {
          fileInput.value = '';
        } catch (e) {}
        updateFileInfo(null);
      }
      detach();
      if (!micSupported) {
        setError(MSG.micOff);
        return;
      }
      // getUserMedia MUST run in the click handler: the user gesture must not be
      // lost across an await.
      var request;
      try {
        request = navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        setError(MSG.record);
        return;
      }
      request.then(beginRecording).catch(function () {
        setError(MSG.record);
      });
    }

    function onMicClick() {
      if (state === STATE.ENCODING) return;
      if (state === STATE.RECORDING) {
        stopRecording();
        return;
      }
      startRecording();
    }

    function onFilePicked() {
      clearError();
      var token = ++pickToken;
      var file = fileInput && fileInput.files && fileInput.files[0];
      if (!file) {
        detach();
        updateFileInfo(null);
        setState(STATE.IDLE, defaultStatus);
        return;
      }
      // Upload and recording are mutually exclusive: last selection wins.
      discardRecording();
      if (!formatOk(file)) {
        fileInput.value = '';
        detach();
        updateFileInfo(null);
        setError(MSG.format);
        return;
      }
      if (file.size > MAX_BYTES) {
        fileInput.value = '';
        detach();
        updateFileInfo(null);
        setError(MSG.size);
        return;
      }
      updateFileInfo(file);
      setState(STATE.IDLE, MSG.probe);
      readFileDuration(file, function (duration) {
        if (token !== pickToken) return;
        if (!isReadableDuration(duration)) {
          // Open Question #3: duration unreadable for this ogg/wav -> accept with
          // a size-only duration guard (format + size already passed above).
          if (!attach(file, null)) return;
          setState(STATE.DONE, MSG.doneUnknown);
          return;
        }
        if (duration > MAX_SECONDS) {
          fileInput.value = '';
          detach();
          updateFileInfo(null);
          setError(MSG.duration);
          return;
        }
        if (!attach(file, duration)) return;
        setState(STATE.DONE, MSG.doneUpload + ' (' + formatSeconds(duration) + ').');
      });
    }

    function onSubmit(e) {
      if (state === STATE.ENCODING) {
        block(e, MSG.encoding);
        return;
      }
      var title = titleInput ? String(titleInput.value || '').trim() : '';
      if (mirrors.title) mirrors.title.value = title;
      if (!title) {
        block(e, MSG.title);
        if (titleInput && titleInput.focus) titleInput.focus();
        return;
      }
      if (lastError) {
        block(e, lastError);
        return;
      }
      if (!hasAudio()) {
        block(e, MSG.missing);
        return;
      }
      // Synchronous re-check from the cached file/duration before letting it through.
      if (attached.file) {
        if (!formatOk(attached.file)) {
          block(e, MSG.format);
          return;
        }
        if (attached.file.size > MAX_BYTES) {
          block(e, MSG.size);
          return;
        }
        if (isReadableDuration(attached.duration) && attached.duration > MAX_SECONDS) {
          block(e, MSG.duration);
        }
      }
    }

    function block(e, message) {
      e.preventDefault();
      e.stopImmediatePropagation();
      showError(message);
      setStatus(message);
    }

    if (titleInput) {
      titleInput.addEventListener('input', function () {
        if (mirrors.title) mirrors.title.value = titleInput.value;
      });
    }
    if (fileInput) fileInput.addEventListener('change', onFilePicked);
    if (micButton) micButton.addEventListener('click', onMicClick);
    form.addEventListener('submit', onSubmit, true);

    if (!micSupported) {
      if (micButton) micButton.style.display = 'none';
      var orEl = document.querySelector('[data-qrear-audio-or]');
      if (orEl) orEl.style.display = 'none';
      defaultStatus = MSG.micOff;
    }
    setState(STATE.IDLE, defaultStatus);
  }

  function findCartForm() {
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    var form = null;
    for (var i = 0; i < forms.length; i++) {
      if (forms[i].querySelector('[name="add"]')) {
        form = forms[i];
        break;
      }
    }
    if (!form && forms.length) form = forms[0];
    return form;
  }

  function ensureMirror(form, key) {
    var name = 'properties[' + key + ']';
    var selector = 'input[name="' + name + '"]';
    var input = form.querySelector(selector);
    if (!input) {
      var scope = document.querySelector('[data-qrear-audio-mirrors]');
      if (scope) input = scope.querySelector(selector);
    }
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
    }
    if (input.parentNode !== form) form.appendChild(input);
    if (key === '_memora_type') input.value = 'audio';
    return input;
  }

  function ensureAudioMirror(form) {
    var input = form.querySelector('input[type="file"][name="properties[audio_url]"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'file';
      input.name = 'properties[audio_url]';
      input.accept = 'audio/mpeg,audio/wav,audio/ogg,.mp3,.wav,.ogg';
      input.style.display = 'none';
      form.appendChild(input);
    }
    return input;
  }

  function extOf(name) {
    var match = /\.([a-z0-9]+)$/i.exec(String(name || ''));
    return match ? match[1].toLowerCase() : '';
  }

  function formatOk(file) {
    if (!file) return false;
    var mime = String(file.type || '').toLowerCase();
    if (ALLOWED_MIMES.indexOf(mime) !== -1) return true;
    return ALLOWED_EXTS.indexOf(extOf(file.name)) !== -1;
  }

  function isReadableDuration(duration) {
    return typeof duration === 'number' && isFinite(duration) && duration > 0;
  }

  function formatSeconds(seconds) {
    var total = Math.round(seconds);
    var minutes = Math.floor(total / 60);
    var rest = total % 60;
    return minutes + ':' + (rest < 10 ? '0' : '') + rest;
  }

  function readFileDuration(file, callback) {
    var url;
    try {
      url = URL.createObjectURL(file);
    } catch (e) {
      callback(NaN);
      return;
    }
    var audio = document.createElement('audio');
    audio.preload = 'metadata';
    var settled = false;
    var timer = setTimeout(function () {
      finish(NaN);
    }, DURATION_PROBE_TIMEOUT);
    function finish(duration) {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        audio.removeAttribute('src');
        audio.load();
      } catch (e) {}
      try {
        URL.revokeObjectURL(url);
      } catch (e) {}
      callback(duration);
    }
    audio.addEventListener('loadedmetadata', function () {
      finish(audio.duration);
    });
    audio.addEventListener('error', function () {
      finish(NaN);
    });
    audio.src = url;
  }

  function isRecordingSupported() {
    return !!(
      typeof window.MediaRecorder !== 'undefined' &&
      window.isSecureContext === true &&
      navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function'
    );
  }

  function pickRecorderMime() {
    if (!window.MediaRecorder || typeof window.MediaRecorder.isTypeSupported !== 'function') return '';
    for (var i = 0; i < RECORDER_MIMES.length; i++) {
      if (window.MediaRecorder.isTypeSupported(RECORDER_MIMES[i])) return RECORDER_MIMES[i];
    }
    return '';
  }

  function encodeRecording(blob) {
    return blob.arrayBuffer().then(function (buffer) {
      var Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) throw new Error('no AudioContext');
      var ctx = new Ctx();
      return decode(ctx, buffer).then(
        function (audioBuffer) {
          var encoded = encodeMp3(audioBuffer);
          try {
            ctx.close();
          } catch (e) {}
          return new Blob(encoded, { type: 'audio/mpeg' });
        },
        function (err) {
          try {
            ctx.close();
          } catch (e) {}
          throw err;
        }
      );
    });
  }

  function decode(ctx, buffer) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      function ok(audioBuffer) {
        if (settled) return;
        settled = true;
        resolve(audioBuffer);
      }
      function fail(err) {
        if (settled) return;
        settled = true;
        reject(err || new Error('decode failed'));
      }
      try {
        var maybe = ctx.decodeAudioData(buffer, ok, fail);
        if (maybe && typeof maybe.then === 'function') maybe.then(ok, fail);
      } catch (e) {
        fail(e);
      }
    });
  }

  function encodeMp3(audioBuffer) {
    if (!window.lamejs || typeof window.lamejs.Mp3Encoder !== 'function') throw new Error('lamejs missing');
    var mono = downmixToInt16(audioBuffer);
    var encoder = new window.lamejs.Mp3Encoder(1, audioBuffer.sampleRate, MP3_BITRATE);
    var parts = [];
    for (var i = 0; i < mono.length; i += MP3_CHUNK) {
      var data = encoder.encodeBuffer(mono.subarray(i, i + MP3_CHUNK));
      if (data && data.length > 0) parts.push(new Int8Array(data));
    }
    var tail = encoder.flush();
    if (tail && tail.length > 0) parts.push(new Int8Array(tail));
    return parts;
  }

  function downmixToInt16(audioBuffer) {
    var channels = audioBuffer.numberOfChannels;
    var length = audioBuffer.length;
    var out = new Int16Array(length);
    if (channels === 1) {
      var only = audioBuffer.getChannelData(0);
      for (var i = 0; i < length; i++) out[i] = toInt16(only[i]);
      return out;
    }
    var acc = new Float32Array(length);
    for (var c = 0; c < channels; c++) {
      var data = audioBuffer.getChannelData(c);
      for (var j = 0; j < length; j++) acc[j] += data[j];
    }
    for (var k = 0; k < length; k++) out[k] = toInt16(acc[k] / channels);
    return out;
  }

  function toInt16(x) {
    if (x < -1) x = -1;
    else if (x > 1) x = 1;
    return x < 0 ? x * 0x8000 : x * 0x7fff;
  }

  window.QrearAudioRecorder = { init: init };
})();
